
import { Options, Vue } from 'vue-class-component';
import { firstCapitalize, formatDateNumber, parsingErrorResponse, formatByCountry } from "@/app/infrastructures/misc/Utils";
import { PickupDashboardController } from "@/app/ui/controllers/PickupDashboardController";
import { PickupShipmentRequest } from "@/data/payload/api/PickupDashboardRequests";
import { CourierPickupShipment } from "@/domain/entities/PickupDashboard";
import PaginationV3 from "@/app/ui/components/pagination-v3/index.vue";
import whatsapp from "@/app/infrastructures/misc/common-library/Whatsapp";
import formatPhoneNumber from "@/app/infrastructures/misc/common-library/v2/FormatPhoneNumber";
import Checkbox from "primevue/checkbox";
import HeaderFilterBar from "@/app/ui/views/pickup-dashboard/monitoring/components/header-filter-bar/index.vue";
import { Range } from "@/app/ui/components/date-range-picker/type";
import { debounce } from "lodash";
import { MainAppController } from "@/app/ui/controllers/MainAppController";
import { ExportApiRequest } from "@/data/payload/api/PickupDashboardApiRequest";
import { ReportController } from "@/app/ui/controllers/ReportController";
import { formatDate } from "@/app/infrastructures/misc/Utils";
import { DownloadReportRequest } from "@/domain/entities/Kulioner";

@Options({
  components: {
    PaginationV3,
    Checkbox,
    HeaderFilterBar
  }
})
class PickupDashboardMonitoringCourierTransferBooking extends Vue {
  search = "";
  loading = false;
  errorCause = "";
  shipmentEntities: CourierPickupShipment = new CourierPickupShipment();
  showImagePreview = false;
  images = [];
  image = "";
  dateRange = new Range();
  courierId = "";
  formatPhoneNumber = formatPhoneNumber;
  formatDate = formatDate;

  mounted() {
    this.fetchBookingEligibleShipments();
    this.courierId = String(this.$route.params.id || "");
  }

  get shipmentPagination() {
    return this.shipmentEntities.pagination;
  }

  get shipmentData() {
    return this.shipmentEntities.data;
  }

  get id(): string {
    return String(this.$route.params.id || "");
  }

  get courierName(): string {
    const name = String(this.$route.query.courier_name || "");
    const displayName = name
      ? name
          .split(" ")
          .map((w) => firstCapitalize(w))
          .join(" ")
      : "";
    return displayName || "-";
  }

  get courierPhone(): string {
    return String(this.$route.query.courier_phone || "-");
  }

  goBack() {
    const path = "/pickup-dashboard/monitoring/courier";
    this.$router.push(path);
  }

  // search
  onSearch(value: string) {
    this.search = value;
    this.shipmentEntities.pagination.page = 1;
    this.fetchBookingEligibleShipments();
  }

  clearSearch() {
    this.search = "";
    this.shipmentEntities.pagination.page = 1;
    this.fetchBookingEligibleShipments();
  }

  onChangeDate(date: any) {
    this.dateRange = date;
    this.shipmentEntities.pagination.page = 1;
    this.fetchBookingEligibleShipments();
  }

  onDownload = debounce(async (date: DownloadReportRequest) => {
    try {
      MainAppController.showLoading();

      await PickupDashboardController.export(
        new ExportApiRequest({
          ...date,
          q: this.search,
          courierId: Number(this.courierId)
        })
      );

      await ReportController.onGetListDownloadReport();
      ReportController.setOpenDownloadRequest(true);
    } catch (err) {
      MainAppController.showErrorMessage(
        parsingErrorResponse(err, "Gagal Download Data !", this.onDownload)
      );
    } finally {
      MainAppController.closeLoading();
    }
  }, 400);

  async fetchBookingEligibleShipments() {
    try {
      this.errorCause = "";
      this.loading = true;
      const res = await PickupDashboardController.getBookingEligibleShipments(
        new PickupShipmentRequest({
          page: this.shipmentPagination.page,
          courierId: Number(this.id),
          status: "CRRPIC,CRRDON",
          q: this.search,
          startDate: formatDateNumber(this.dateRange.start),
          endDate: formatDateNumber(this.dateRange.end),
          limit: 20
        })
      );
      this.shipmentEntities = res;
    } catch (err) {
      this.shipmentEntities = new CourierPickupShipment();
      const response = (err as { response?: unknown }).response;
      this.errorCause = response ? "server" : "internet";
    } finally {
      this.loading = false;
    }
  }

  toPageV3(value: number) {
    if (value === this.shipmentPagination.page) {
      return;
    }
    this.shipmentEntities.pagination.page = value;
    this.fetchBookingEligibleShipments();
  }

  mapShipmentType(item: string) {
    const label: any = {
      AGGREGATOR: "Aggregator",
      MARKETPLACE: "Marketplace",
      CUSTOMER_APP: "Customer App",
      CORPORATE: "Corporate"
    };

    return label[item] ?? "-";
  }

  onBooking(item: string[]) {
    const shipmentIds = item.join(",");
    this.$router.push(`/booking-v2/create/by-shipment-id?bulk=${shipmentIds}`);
  }

  showProof(item: any) {
    if (item.proofs.length) {
      this.image = item.proofs[0];
      this.images = item.proofs.map((item: any) => {
        return {
          image: item,
          url: item
        };
      });
      this.showImagePreview = true;
    }
  }

  onClickWhatsapp(phoneNumber: string) {
    whatsapp(phoneNumber);
  }

  get showBookingButton() {
    return this.shipmentData.some((item: any) => item.isChecked);
  }

  onCheckAll(state: boolean) {
    this.shipmentData
      .filter((item: any) => item.isAbleToBooking)
      .forEach((item: any) => {
        item.isChecked = state;
      });
  }

  get isAllCheked() {
    return this.shipmentData
      .filter((item: any) => item.isAbleToBooking)
      .every((item: any) => item.isChecked);
  }

  closeImagePreview() {
    this.images = [];
    this.showImagePreview = false;
  }

  get checkedShipmentId() {
    return this.shipmentData
      .filter((item) => item.isAbleToBooking && item.isChecked)
      .map((item) => item.shipmentId);
  }

  get emptyData() {
    const isFilterActive =
      this.search || this.dateRange.start || this.dateRange.end;
    return {
      img: isFilterActive ? "ill_spot_search" : "noData_empty",
      title: isFilterActive
        ? this.$t("pickupDashboard.emptyState.searchNotFound")
        : this.$t("pickupDashboard.emptyState.noPackage"),
      message: isFilterActive
        ? this.$t("pickupDashboard.emptyState.searchNotFoundHint")
        : this.$t("pickupDashboard.emptyState.noPackageHint")
    };
  }

  fmtQty(val: number | string) {
    return formatByCountry(Number(val || 0), { type: "quantity" });
  }
}
export default PickupDashboardMonitoringCourierTransferBooking;
